import { SafeAny } from '@/common/types';
import type { GraphPoint, Selection } from '@/common/ui';
import { ScaleLinear } from 'd3';

export const BAD_DATA_LIMITS = {
  Heart_rate: 0,
  SpO2: 125,
  Position: 400
};

export type BadDataSignal = keyof typeof BAD_DATA_LIMITS;

export function isBadDataValueBySignal(signal: BadDataSignal, value: number): boolean {
  const limit = BAD_DATA_LIMITS[signal];

  if (signal === 'SpO2') {
    return value >= limit;
  }

  return value === limit;
}

export function getBadSegments(
  data: GraphPoint<SafeAny>[]
): Array<[GraphPoint<SafeAny>, GraphPoint<SafeAny>]> {
  const badSegments: Array<[GraphPoint<SafeAny>, GraphPoint<SafeAny>]> = [];
  let badSegmentStart: GraphPoint<SafeAny> | null = null;

  data.forEach((d, i) => {
    if (d.y === null && badSegmentStart === null) {
      badSegmentStart = d;
    } else if (d.y !== null && badSegmentStart !== null) {
      badSegments.push([badSegmentStart, data[i - 1]]);
      badSegmentStart = null;
    }
  });

  if (badSegmentStart !== null) {
    badSegments.push([badSegmentStart, data[data.length - 1]]);
  }

  return badSegments;
}

export function drawBadDataLines(
  group: Selection,
  segments: Array<[GraphPoint<SafeAny>, GraphPoint<SafeAny>]>,
  xScale: (x: number) => number,
  yScale: ScaleLinear<any, any, any>,
  color?: string
) {
  group.selectAll('.bad-segment-line').remove();

  if (!segments?.length) return;

  segments.forEach(([start, end]) => {
    group
      .append('line')
      .attr('class', 'bad-segment-line')
      .attr('x1', xScale(start.x))
      .attr('x2', xScale(start.x))
      .attr('y1', yScale(yScale.domain()[0]))
      .attr('y2', yScale(yScale.domain()[1]))
      .attr('stroke', color || 'darkgrey')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2,2');

    group
      .append('line')
      .attr('class', 'bad-segment-line')
      .attr('x1', xScale(end.x))
      .attr('x2', xScale(end.x))
      .attr('y1', yScale(yScale.domain()[0]))
      .attr('y2', yScale(yScale.domain()[1]))
      .attr('stroke', color || 'darkgrey')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2,2');
  });
}

/**
 * Draws small gray circular markers for null (bad) data points.
 * @param group - D3 selection (graph group)
 * @param data - signal data array
 * @param xScale - x scale function
 * @param yScale - y scale function
 */
export function drawBadDataMarkers(
  group: Selection,
  data: GraphPoint<SafeAny>[],
  xScale: (x: number) => number,
  yScale: ScaleLinear<any, any, any>,
  color?: string
): void {
  const badDataPoints = data.filter((d) => d.y === null);

  const badMarkers = group.selectAll('circle.bad-marker').data(badDataPoints);

  badMarkers
    .enter()
    .append('circle')
    .classed('bad-marker', true)
    .merge(badMarkers as SafeAny)
    .attr('cx', (d) => xScale(d.x))
    .attr('cy', () => yScale(yScale.domain()[1]))
    .attr('r', 1)
    .attr('fill', color || 'darkgrey');

  badMarkers.exit().remove();

  group.selectAll('circle.bad-marker, .bad-segment-line').raise();
}

export function applyArtifactBadData(
  data: GraphPoint<SafeAny>[],
  artifactEvents: Array<{ start: string; duration: number }>
): GraphPoint<SafeAny>[] {
  if (!artifactEvents?.length) return data;

  const artifactRanges: Array<[number, number]> = artifactEvents.map((event) => {
    const startUnix = new Date(event.start).getTime() / 1000;
    return [startUnix, startUnix + event.duration];
  });

  return data.map((point) => {
    if (point.y === null) return point;
    const isInArtifact = artifactRanges.some(
      ([rangeStart, rangeEnd]) => point.x >= rangeStart && point.x < rangeEnd
    );
    return isInArtifact ? { ...point, y: null } : point;
  });
}
